import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  getPlayground, listIntegrations, renamePlayground, runComparePanels, listRuns, getRun,
  savePlaygroundState, importSpan, createPlayground,
} from 'features/playground/api/playgroundApi';
import {
  listPrompts, getPrompt as getPromptDetail, createPrompt, createVersion as createPromptVersion,
  listLabels, addLabel,
} from 'features/prompts/api/promptsApi';
import ComparePanel from 'features/playground/components/ComparePanel';
import DecisionPanel, { effectiveDecisionModel } from 'features/playground/components/DecisionPanel';
import { listDecisionIntegrations, decisionCatalog, decide } from 'shared/decision/decisionApi';
import { toApi, hasProblems, newQuestion } from 'shared/decision/questions';
import { parseState } from 'features/playground/lib/decisionPresets';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';
import OutputColumn, { ScoringToggle } from 'features/playground/components/OutputColumn';
import PopMenu from 'features/playground/components/PopMenu';
import {
  activityToolbarButtonClass, primaryButtonClass, menuItemClass,
} from 'features/playground/styles';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import SpanImportModal, { spanToMessages } from 'features/playground/components/SpanImportModal';
import SaveVersionModal from 'features/prompts/components/SaveVersionModal';
import { newMessage, reconcileVariables, textBlock } from 'features/prompts/lib/promptDraft';
import { useCatalogModels } from 'shared/hooks/useCatalogModels';
import { modelFromPromptVersion } from 'features/playground/lib/promptImport';
import { useHideGlobalTopBar } from 'shared/context/BreadcrumbContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';

const DEFAULT_NAME = 'Untitled playground';

let PANEL_SEQ = 0;
const uid = () => { PANEL_SEQ += 1; return `p${PANEL_SEQ}_${PANEL_SEQ * 7 + 3}`; };

const integrationModels = (integ) => {
  const list = Array.isArray(integ?.config?.models) ? integ.config.models : [];
  if (list.length) return list;
  return integ?.default_model ? [integ.default_model] : [];
};

// A fresh model row starts with NO provider chosen — the model dropdown + params
// only appear once the user picks one (per the requested flow).
const emptyModel = () => ({ credential_id: '', provider: '', model: '', temperature: 0.2, max_tokens: '' });

const newPanel = (title, mode = 'manual') => ({
  id: uid(),
  title: title || '',
  mode, // manual | span | prompt
  source: null, // { promptId, promptName, versionNo } once a prompt is imported
  source_span_id: null,
  baseline: null, // imported version snapshot the save modal diffs against
  messages: [newMessage('system')],
  variables: [],
  varValues: {},
  tools: [],
  response_format: null,
  model: emptyModel(),
  saving: false,
  dirty: false,
  savedNote: null,
});

/**
 * A Decision task: a state and typed questions answered by a decision model
 * (Jev, Trodo). Runs through /decision-models/decide, never the chat
 * run-compare engine; persisted in the saved state like every other task.
 */
const newDecisionPanel = (title) => ({
  id: uid(),
  title: title || '',
  mode: 'decision',
  state: '',
  questions: [newQuestion('noul', [])],
  decision: { integration_id: '', provider: '', model: '' },
  allow_abstain: true,
  messages: [],
  result: null,
});
const isDecisionPanel = (p) => p?.mode === 'decision';

/** A decide() failure as one line; a rate limit says to wait. */
function decisionError(e) {
  if (e?.code === 'rate_limited') return 'The model is rate limited. Wait a minute and run again.';
  if (e?.code === 'overloaded') return 'The model is overloaded. Wait a moment and run again.';
  return e?.message || 'The model did not answer.';
}

/** Ensure every message's content is a text-block array (span import gives strings). */
const toStructured = (msgs) => (Array.isArray(msgs) ? msgs : []).map((m) => ({
  role: m.role || 'user',
  content: Array.isArray(m.content) ? m.content : [textBlock(String(m.content || ''))],
}));

/** A panel's model config → the shape the prompt registry stores on a version. */
function buildModelConfig(m = {}) {
  // provider/model are optional on a version, but an EMPTY string is rejected
  // (`unsupported ''`) — omit them entirely when unset rather than sending ''.
  const cfg = {};
  if (m.provider) cfg.provider = m.provider;
  if (m.model) cfg.model = m.model;
  const numIf = (k) => { if (m[k] !== '' && m[k] != null) cfg[k] = Number(m[k]); };
  if (m.temperature !== '' && m.temperature != null) cfg.temperature = Number(m.temperature) || 0;
  numIf('max_tokens');
  numIf('top_p');
  numIf('frequency_penalty');
  numIf('presence_penalty');
  if (Array.isArray(m.stop) && m.stop.filter((s) => s !== '').length) cfg.stop = m.stop.filter((s) => s !== '');
  if (m.tool_choice && m.tool_choice !== 'none') cfg.tool_choice = m.tool_choice;
  if (m.use_cache) cfg.use_cache = true;
  // The prompt registry stores a UUID credential_id; the playground's integration
  // id is numeric, so only carry it through when it's actually a UUID. Otherwise
  // the saved version keeps just provider+model (the app supplies creds at fetch).
  if (m.credential_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(m.credential_id))) {
    cfg.credential_id = m.credential_id;
  }
  return cfg;
}

/** The persisted shape of the bench: results are run output, not edits. */
const sanitizePanels = (list) => list.map((p) => {
  const rest = { ...p };
  delete rest.saving; delete rest.savedNote; // transient UI only
  return rest;
});
/** What "unsaved" is measured against: name, tasks (minus run output) and scoring. */
const snapshotOf = (name, panels, scoring) => JSON.stringify({
  name: (name || '').trim(),
  scoring: scoring === true,
  panels: sanitizePanels(panels).map(({ result, ...rest }) => rest), // eslint-disable-line no-unused-vars
});

/** A name that says nothing is not a name: drafts are saved only once really named. */
const isPlaceholderName = (n) => !String(n || '').trim() || /^untitled\b/i.test(String(n).trim());

export default function PlaygroundEditorPage({ playgroundId }) {
  // /playground/new is a draft: nothing exists on the server until it is
  // named and saved (or run, which saves it first).
  const isDraft = playgroundId === 'new';
  const { confirm: appConfirm } = useAppDialog();
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);
  const { team } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [pg, setPg] = useState(null);
  const [name, setName] = useState('');
  const [integrations, setIntegrations] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [panels, setPanels] = useState([]);
  // Opt-in AI Scoring, remembered with the playground's saved state.
  const [scoring, setScoring] = useState(false);
  const [runningIds, setRunningIds] = useState([]);
  const [spanImportPanelId, setSpanImportPanelId] = useState(null);
  const [teamLabels, setTeamLabels] = useState([]);
  const [saveModal, setSaveModal] = useState(null); // { panelId, draft, baseline, existingName, versions }
  const [error, setError] = useState(null);
  // Decision models load only once a Decision task is on the bench.
  const [decisionModels, setDecisionModels] = useState({ status: 'idle', integrations: [], catalog: [] });
  const [problemIds, setProblemIds] = useState([]);
  const nameRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState(false);
  // Nothing is saved until the user presses Save: this is what the bench looked
  // like when it was loaded / last saved, and "unsaved" is any difference.
  const [savedSnap, setSavedSnap] = useState(null);

  useHideGlobalTopBar(true);

  const modelsForCredential = useCatalogModels(null, integrations);

  // ── load ──
  const restoreCompareRun = (run, promptList) => {
    const restored = compareRunPanels(run, promptList);
    setPanels(restored);
    return restored;
  };
  const compareRunPanels = (run, promptList) => {
    const inputs = run.input?.panels || [];
    const models = run.models || [];
    const outs = run.results?.panels || [];
    const byName = new Map((promptList || []).map((p) => [p.name, p]));
    return inputs.map((pi, i) => {
      const owner = pi.prompt_ref?.name ? byName.get(pi.prompt_ref.name) : null;
      return {
        id: uid(),
        title: pi.title || '',
        mode: pi.prompt_ref?.name ? 'prompt' : 'manual',
        source: pi.prompt_ref?.name
          ? { promptId: owner?.id || null, promptName: pi.prompt_ref.name, versionNo: pi.prompt_ref.version ?? null }
          : null,
        source_span_id: null,
        messages: (pi.messages && pi.messages.length) ? pi.messages : [newMessage('system')],
        variables: pi.variables || [],
        varValues: pi.variable_values || {},
        tools: pi.tools || [],
        response_format: pi.response_format ?? null,
        model: models[i] || emptyModel(),
        result: outs[i] || null,
        saving: false, dirty: false, savedNote: null,
      };
    });
  };

  // The draft this page just saved as a playground. Its URL changes to the new
  // id, which reloads; the bench on screen IS what was saved, and a run started
  // with the save may already have answered, so the reload must not replace it.
  const adoptedIdRef = useRef(null);

  const load = useCallback(async () => {
    setError(null);
    if (isDraft) {
      try {
        const [ints, promptList, labels] = await Promise.all([
          listIntegrations(), listPrompts().catch(() => []), listLabels().catch(() => []),
        ]);
        setIntegrations(ints); setPrompts(promptList); setTeamLabels(labels);
      } catch (e) {
        setError(e?.response?.data?.error || e.message);
      }
      const first = [newPanel('Base task')];
      setPg({ id: null, name: '' }); setName('');
      setPanels(first);
      setSavedSnap(snapshotOf('', first, false));
      return;
    }
    try {
      const [data, ints, promptList, runs, labels] = await Promise.all([
        getPlayground(playgroundId), listIntegrations(),
        listPrompts().catch(() => []), listRuns(playgroundId).catch(() => []),
        listLabels().catch(() => []),
      ]);
      setPg(data); setName(data.name || ''); setIntegrations(ints); setPrompts(promptList); setTeamLabels(labels);
      if (adoptedIdRef.current === playgroundId) {
        adoptedIdRef.current = null;
        return;
      }

      // The last saved state is authoritative — restore the bench exactly as saved.
      const saved = data.state;
      if (saved && Array.isArray(saved.panels) && saved.panels.length) {
        // Seeded / older states may carry plain-string message content.
        const fixMsg = (m) => (typeof m?.content === 'string' ? { ...m, content: [textBlock(m.content)] } : m);
        const restored = saved.panels.map((p) => ({
          ...p, messages: (p.messages || []).map(fixMsg), saving: false, savedNote: null,
        }));
        setPanels(restored);
        setScoring(saved.scoring === true);
        setSavedSnap(snapshotOf(data.name, restored, saved.scoring === true));
        return;
      }
      // Legacy fallback: rebuild from the last recorded run.
      const latest = (runs || []).find((r) => r.mode === 'compare');
      if (latest) {
        const full = await getRun(playgroundId, latest.run_no).catch(() => null);
        if (full) { setSavedSnap(snapshotOf(data.name, restoreCompareRun(full, promptList), false)); return; }
      }
      // A new playground starts with a single task; the user adds more if they want.
      const first = [newPanel('Base task')];
      setPanels(first);
      setSavedSnap(snapshotOf(data.name, first, false));
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  }, [playgroundId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const hasDecision = panels.some(isDecisionPanel);
  useEffect(() => {
    if (!hasDecision || decisionModels.status !== 'idle') return;
    setDecisionModels((prev) => ({ ...prev, status: 'loading' }));
    const teamId = getSelectedTeamNumericId();
    Promise.all([
      listDecisionIntegrations(teamId).catch(() => []),
      decisionCatalog(teamId).catch(() => []),
    ]).then(([ints, catalog]) => setDecisionModels({ status: 'done', integrations: ints, catalog }));
  }, [hasDecision, decisionModels.status]);

  const panelsRef = useRef(panels);
  panelsRef.current = panels;
  const scoringRef = useRef(scoring);
  scoringRef.current = scoring;

  const dirty = savedSnap !== null && snapshotOf(name, panels, scoring) !== savedSnap;
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  // Leaving the tab with unsaved edits asks first.
  useEffect(() => {
    const onUnload = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  // Prefill from the Evaluate launcher (?prompt=<name>) into the first task.
  const prefillDone = useRef(false);
  useEffect(() => {
    if (prefillDone.current || !prompts.length) return;
    const promptQ = new URLSearchParams(location.search).get('prompt');
    if (!promptQ) return;
    prefillDone.current = true;
    const p = prompts.find((x) => x.name === promptQ);
    if (!p) return;
    (async () => {
      try {
        const detail = await getPromptDetail(p.id);
        const vs = detail.versions || [];
        const v = vs.find((x) => x.version_no === detail.current_version) || vs[0];
        setPanels((prev) => {
          const next = [...prev];
          next[0] = {
            ...(next[0] || newPanel('Base task')),
            mode: 'prompt',
            source: { promptId: p.id, promptName: detail.name, versionNo: v?.version_no ?? null },
            messages: (v?.messages && v.messages.length) ? v.messages : [newMessage('system')],
            variables: v?.variables || [], varValues: {},
            tools: v?.tools || [], response_format: v?.response_format ?? null,
            // Same guard as ComparePanel: a prompt may name a provider we hold
            // no key for, so adopt only what this team can actually call.
            model: modelFromPromptVersion(v?.model, {
              integrations, modelsForCredential, current: next[0]?.model,
            }),
            result: null,
          };
          return next;
        });
      } catch (_) { /* best-effort */ }
    })();
  }, [prompts, integrations, location.search]);

  // Import an LLM span into a NEW task (from the "Open in playground" button on a
  // span popup: ?importSpan=<spanId>). One-shot; replaces a pristine default task
  // if the playground is otherwise empty, else appends.
  const importSpanDone = useRef(false);
  useEffect(() => {
    if (importSpanDone.current || !pg) return;
    const spanQ = new URLSearchParams(location.search).get('importSpan');
    if (!spanQ) return;
    importSpanDone.current = true;
    (async () => {
      try {
        const imported = await importSpan(spanQ);
        let modelPatch = emptyModel();
        if (imported.provider) {
          const match = integrations.find((x) => x.provider === imported.provider);
          if (match) {
            const list = integrationModels(match);
            modelPatch = {
              credential_id: match.id, provider: match.provider,
              model: (imported.model && list.includes(imported.model)) ? imported.model : (list[0] || imported.model || ''),
              temperature: imported.temperature ?? 0.2, max_tokens: '',
            };
          }
        }
        // Map the span's turns → messages (falls back to one message if unformatted).
        const msgs = toStructured(spanToMessages(imported));
        const spanPanel = {
          ...newPanel('From span', 'span'),
          source_span_id: imported.source_span_id || spanQ,
          messages: msgs.length ? msgs : [newMessage('user')],
          model: modelPatch,
        };
        const isPristine = (p) => p && p.mode === 'manual' && !p.source && !p.source_span_id
          && !p.result && !p.model?.credential_id
          && (p.messages || []).every((m) => !(m.content || []).some((b) => b.type === 'text' && b.text && b.text.trim()));
        setPanels((prev) => (prev.length === 1 && isPristine(prev[0]) ? [spanPanel] : [...prev, spanPanel]));
      } catch (e) {
        setError(`Could not import the span: ${e?.response?.data?.error || e.message}`);
      } finally {
        // Drop the query param so a refresh doesn't re-import.
        navigate(`/${encodeURIComponent(team)}/playground/${playgroundId}`, { replace: true });
      }
    })();
  }, [pg, integrations, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── panel ops ──
  const updatePanel = useCallback((id, patch) => {
    setPanels((prev) => prev.map((p) => (p.id === id
      ? { ...p, ...(typeof patch === 'function' ? patch(p) : patch) } : p)));
  }, []);

  const addPanelOfType = (mode) => {
    const p = mode === 'decision' ? newDecisionPanel('Decision task') : newPanel('Comparison task', mode);
    setPanels((prev) => [...prev, p]);
    if (mode === 'span') setSpanImportPanelId(p.id);
  };
  const removePanel = (id) => setPanels((prev) => (prev.length > 1 ? prev.filter((p) => p.id !== id) : prev));
  const duplicatePanel = (id) => setPanels((prev) => {
    const idx = prev.findIndex((p) => p.id === id);
    if (idx === -1) return prev;
    const src = prev[idx];
    const copy = { ...src, id: uid(), result: null, savedNote: null, title: src.title ? `${src.title} copy` : 'Comparison task' };
    const next = [...prev];
    next.splice(idx + 1, 0, copy);
    return next;
  });

  // ── span import (targets one panel) ──
  const applyImportedSpan = (imported) => {
    if (!spanImportPanelId) return;
    let modelPatch = {};
    if (imported.provider) {
      const match = integrations.find((x) => x.provider === imported.provider);
      if (match) {
        const list = integrationModels(match);
        modelPatch = {
          credential_id: match.id, provider: match.provider,
          model: (imported.model && list.includes(imported.model)) ? imported.model : (list[0] || imported.model || ''),
          temperature: imported.temperature ?? 0.2, max_tokens: '',
        };
      }
    }
    updatePanel(spanImportPanelId, (p) => ({
      mode: 'span',
      source: null,
      source_span_id: imported.source_span_id || null,
      messages: toStructured(imported.messages).length ? toStructured(imported.messages) : [newMessage('user')],
      variables: [], varValues: {},
      model: Object.keys(modelPatch).length ? modelPatch : p.model,
      result: null,
    }));
    setSpanImportPanelId(null);
  };

  // ── run ──
  const toPayload = (p) => ({
    title: p.title || '',
    prompt_ref: p.source?.promptName ? { name: p.source.promptName, version: p.source.versionNo } : null,
    messages: p.messages,
    variables: reconcileVariables(p.messages, p.variables),
    variable_values: p.varValues || {},
    response_format: p.response_format ?? null,
    model: p.model,
  });

  const askForName = () => {
    setNameError(true);
    nameRef.current?.focus();
  };

  // A draft becomes a playground here: named, with its bench. No name, no dialog:
  // the name field in the header turns red instead.
  const saveDraft = async () => {
    if (!isDraft) return playgroundId;
    const trimmed = (name || '').trim();
    if (isPlaceholderName(trimmed)) { askForName(); return null; }
    setSaving(true);
    try {
      const created = await createPlayground(trimmed);
      await savePlaygroundState(created.id, { panels: sanitizePanels(panelsRef.current), scoring: scoringRef.current });
      setError(null);
      setSavedSnap(snapshotOf(trimmed, panelsRef.current, scoringRef.current));
      adoptedIdRef.current = String(created.id);
      navigate(`/${encodeURIComponent(team)}/playground/${created.id}`, { replace: true, state: { adoptedDraft: true } });
      return created.id;
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Saving an existing playground: name (if changed) and the whole bench, once.
  const saveExisting = async () => {
    const trimmed = (name || '').trim();
    if (!trimmed) { askForName(); return; }
    setSaving(true);
    try {
      if (trimmed !== pg.name) {
        const updated = await renamePlayground(playgroundId, trimmed);
        setPg((prev) => ({ ...prev, name: updated.name })); setName(updated.name);
      }
      await savePlaygroundState(playgroundId, { panels: sanitizePanels(panels), scoring });
      setError(null);
      setSavedSnap(snapshotOf(trimmed, panels, scoring));
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };
  const save = () => (isDraft ? saveDraft() : saveExisting());

  const leaveTo = async (path) => {
    if (dirtyRef.current && !(await appConfirm('Your changes to this playground are not saved.', {
      title: 'Leave without saving?', confirmLabel: 'Leave', danger: true,
    }))) return;
    navigate(path);
  };
  const leaveToList = (e) => { e.preventDefault(); return leaveTo(`/${encodeURIComponent(team)}/playground`); };
  const modelsSettingsPath = `/${encodeURIComponent(team)}/settings/models`;

  // Chat tasks run together through run-compare; Decision tasks each call
  // /decision-models/decide. Both start after the same checks and draft save.
  const runPanels = async (list) => {
    setError(null);
    if (!list.length) return;
    const chat = list.filter((p) => !isDecisionPanel(p));
    const dec = list.filter(isDecisionPanel);
    if (chat.find((p) => !p.model?.credential_id)) { setError('Every task needs a provider + model before running.'); return; }
    if (dec.length) {
      if (!decisionModels.integrations.length) { setError('Add a Jev or Hartley key in Settings → Models'); return; }
      const noState = dec.filter((p) => !String(p.state || '').trim());
      const badQs = dec.filter((p) => hasProblems(p.questions));
      setProblemIds(badQs.map((p) => p.id));
      if (noState.length) { setError('Every Decision task needs a state.'); return; }
      if (badQs.length) { setError('Fix the questions marked below before running.'); return; }
    }
    const targetId = await saveDraft();
    if (!targetId) return;
    await Promise.all([
      chat.length ? runChatPanels(targetId, chat) : null,
      ...dec.map(runDecisionPanel),
    ]);
  };

  const runChatPanels = async (targetId, list) => {
    const ids = list.map((p) => p.id);
    setRunningIds((prev) => [...new Set([...prev, ...ids])]);
    try {
      const { results } = await runComparePanels(targetId, list.map(toPayload), { score: scoring });
      setPanels((prev) => prev.map((p) => {
        const idx = ids.indexOf(p.id);
        return idx === -1 ? p : { ...p, result: results.panels[idx] || null };
      }));
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally {
      setRunningIds((prev) => prev.filter((x) => !ids.includes(x)));
    }
  };

  const runDecisionPanel = async (p) => {
    const { integ, model } = effectiveDecisionModel(p, decisionModels.integrations, decisionModels.catalog);
    const questions = toApi(p.questions);
    setRunningIds((prev) => [...new Set([...prev, p.id])]);
    try {
      const r = await decide(getSelectedTeamNumericId(), {
        integration_id: integ.id,
        ...(model ? { model } : {}),
        state: parseState(p.state),
        questions,
        allow_abstain: p.allow_abstain !== false,
      });
      updatePanel(p.id, { result: { ok: true, ...r, questions } });
    } catch (e) {
      updatePanel(p.id, { result: { ok: false, error: decisionError(e) } });
    } finally {
      setRunningIds((prev) => prev.filter((x) => x !== p.id));
    }
  };

  const runAll = () => runPanels(panels);
  const runOne = (id) => { const p = panels.find((x) => x.id === id); if (p) runPanels([p]); };

  // ── save a task to the prompt registry (opens the shared save modal with a
  //    diff + commit message + label, exactly like the prompt editor) ──
  // Drop empty message turns before saving — the registry rejects a version with
  // an empty message (invalid_content), and an empty turn carries nothing anyway.
  const nonEmptyMessages = (msgs) => (msgs || []).filter(
    (m) => m.type === 'placeholder'
      || (m.content || []).some((b) => b.type === 'text' && b.text && b.text.trim()),
  );
  const buildDraft = (p) => {
    const messages = nonEmptyMessages(p.messages);
    return {
      messages,
      model: buildModelConfig(p.model),
      variables: reconcileVariables(messages, p.variables),
      tools: p.tools || [],
      response_format: p.response_format ?? null,
    };
  };

  const openSaveModal = async (id) => {
    const p = panels.find((x) => x.id === id);
    if (!p) return;
    setError(null);
    let versions = [];
    let baseline = p.baseline || null;
    if (p.source?.promptId) {
      try {
        const detail = await getPromptDetail(p.source.promptId);
        versions = detail.versions || [];
        if (!baseline) {
          const v = versions.find((x) => x.version_no === p.source.versionNo)
            || versions.find((x) => x.version_no === detail.current_version) || versions[0];
          if (v) baseline = { messages: v.messages || [], model: v.model || {}, variables: v.variables || [], tools: v.tools || [], response_format: v.response_format ?? null, version_hash: v.version_hash };
        }
      } catch (_) { /* new/unresolved prompt → diff against nothing */ }
    }
    setSaveModal({ panelId: id, draft: buildDraft(p), baseline, existingName: p.source?.promptName || null, versions });
  };

  const doSaveVersion = async ({ name, commit_message, labels }) => {
    const sm = saveModal;
    const p = panels.find((x) => x.id === sm.panelId);
    if (!p) throw new Error('This task no longer exists.');
    let promptId = p.source?.promptId;
    let promptName = p.source?.promptName;
    if (!promptId) {
      if (!name) throw new Error('Give the prompt a name.');
      try {
        const created = await createPrompt(name);
        promptId = created.id; promptName = name;
      } catch (e) {
        if (e?.response?.data?.error === 'name_taken') {
          const all = await listPrompts();
          promptId = all.find((x) => x.name === name)?.id; promptName = name;
        } else { throw e; }
      }
      if (!promptId) throw new Error('Could not resolve prompt');
    }
    const res = await createPromptVersion(promptId, {
      ...buildDraft(p),
      labels: labels && labels.length ? labels : [],
      commit_message,
    });
    const newBaseline = { ...buildDraft(p), version_hash: res.version_hash };
    updatePanel(sm.panelId, {
      mode: 'prompt', dirty: false, baseline: newBaseline,
      source: { promptId, promptName, versionNo: res.version_no ?? p.source?.versionNo ?? null },
      savedNote: res.unchanged ? 'No changes' : `Saved v${res.version_no}`,
    });
    listPrompts().then(setPrompts).catch(() => {});
    setTimeout(() => updatePanel(sm.panelId, { savedNote: null }), 4000);
    return res;
  };

  if (!pg) {
    if (error) return <p role="alert" className="p-6 text-[12px] text-[color:var(--feedback-error)]">{error}</p>;
    return <PageLoader label="Loading playground" />;
  }

  const anyRunning = runningIds.length > 0;
  const hasAnyResult = panels.some((p) => p.result) || anyRunning;

  return (
    <div className="flex h-full min-h-0 flex-col bg-[color:var(--surface-base)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* top bar */}
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-[color:var(--border-default)] px-4">
        <Link
          to={`/${encodeURIComponent(team)}/playground`} onClick={leaveToList}
          className="shrink-0 whitespace-nowrap rounded text-[13px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]"
        >
          Playground
        </Link>
        <ChevronRight className="h-3 w-3 shrink-0 text-[color:var(--text-muted)]" aria-hidden />
        <input
          ref={nameRef} autoFocus={isDraft} // eslint-disable-line jsx-a11y/no-autofocus
          type="text" value={name}
          onChange={(e) => { setName(e.target.value); setNameError(false); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canWrite) save();
            if (e.key === 'Escape' && !isDraft) { setName(pg.name || ''); setNameError(false); e.currentTarget.blur(); }
          }}
          placeholder={isDraft ? 'Name this playground' : DEFAULT_NAME} aria-label="Playground name"
          aria-invalid={nameError || undefined} aria-describedby={nameError ? 'playground-name-error' : undefined}
          className={`h-8 min-w-0 rounded-md border bg-transparent px-1.5 text-[13px] font-semibold text-[color:var(--text-primary)] placeholder:font-normal placeholder:text-[color:var(--text-disabled)] focus:outline-none ${
            nameError
              ? 'border-[color:var(--feedback-error)]'
              : 'border-transparent hover:border-[color:var(--border-default)] focus:border-[color:var(--border-strong)]'
          }`}
          style={{ width: `${Math.max(6, (name || (isDraft ? 'Name this playground' : '')).length) + 3}ch` }}
        />
        {nameError && (
          <span id="playground-name-error" role="alert" className="shrink-0 text-[12px] text-[color:var(--feedback-error)]">
            Please enter a name to save
          </span>
        )}
        <div className="flex-1" />
        {dirty && !isDraft && <span className="shrink-0 text-[12px] text-[color:var(--text-muted)]">Unsaved changes</span>}
        {canWrite && (
          <button type="button" onClick={save} disabled={saving || (!isDraft && !dirty)} className={primaryButtonClass}>
            {saving ? 'Saving' : 'Save'}
          </button>
        )}
        <ScoringToggle enabled={scoring} onChange={setScoring} disabled={anyRunning} />
        <PopMenu
          label="Add a task"
          width={160}
          renderTrigger={({ ref, onClick, ariaProps }) => (
            <button ref={ref} type="button" onClick={onClick} title="Add a task" className={activityToolbarButtonClass} {...ariaProps}>
              Add task
            </button>
          )}
        >
          {(close) => [['manual', 'Manual'], ['span', 'From span'], ['prompt', 'From prompt'], ['decision', 'Decision']].map(([key, label]) => (
            <button key={key} type="button" role="menuitem" className={menuItemClass}
              onClick={() => { close(); addPanelOfType(key); }}>
              {label}
            </button>
          ))}
        </PopMenu>
        <button type="button" onClick={runAll} disabled={anyRunning || !panels.length} className={activityToolbarButtonClass}>
          {anyRunning ? 'Running' : 'Run all'}
        </button>
      </div>

      {error && <p role="alert" className="px-4 pt-3 text-[12px] text-[color:var(--feedback-error)]">{error}</p>}
      {integrations.length === 0 && panels.some((p) => !isDecisionPanel(p)) && (
        <p className="px-4 pt-3 text-[12px] text-[color:var(--text-muted)]">
          No linked model providers yet. Add one under{' '}
          <button type="button" className="text-[color:var(--text-primary)] underline underline-offset-2" onClick={() => navigate(`/${encodeURIComponent(team)}/integrations`)}>Integrations</button>{' '}to run tasks.
        </p>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* tasks: columns split by hairlines; each scrolls its own content */}
        <div className="flex min-h-0 flex-1 divide-x divide-[color:var(--border-default)] overflow-x-auto">
          {panels.map((panel, i) => (isDecisionPanel(panel) ? (
            <DecisionPanel
              key={panel.id}
              panel={panel} index={i}
              integrations={decisionModels.integrations} catalog={decisionModels.catalog}
              loadingModels={decisionModels.status !== 'done'}
              settingsHref={modelsSettingsPath} onOpenSettings={() => leaveTo(modelsSettingsPath)}
              readOnly={!canWrite} showProblems={problemIds.includes(panel.id)}
              canRemove={panels.length > 1} running={runningIds.includes(panel.id)}
              onChange={(patch) => updatePanel(panel.id, patch)}
              onRemove={() => removePanel(panel.id)}
              onDuplicate={() => duplicatePanel(panel.id)}
              onRun={() => runOne(panel.id)}
            />
          ) : (
            <ComparePanel
              key={panel.id}
              panel={panel} index={i}
              integrations={integrations} modelsForCredential={modelsForCredential} prompts={prompts}
              canRemove={panels.length > 1} running={runningIds.includes(panel.id)}
              onChange={(patch) => updatePanel(panel.id, patch)}
              onRemove={() => removePanel(panel.id)}
              onDuplicate={() => duplicatePanel(panel.id)}
              onRun={() => runOne(panel.id)}
              onSavePrompt={() => openSaveModal(panel.id)}
              onRequestSpanImport={() => setSpanImportPanelId(panel.id)}
            />
          )))}
        </div>

        {/* results: one column per task, aligned under it. Capped so it never
            eats the tasks region; its own content scrolls. */}
        {hasAnyResult && (
          <div className="flex max-h-[45%] shrink-0 flex-col border-t border-[color:var(--border-default)]">
            <h2 className="flex h-10 shrink-0 items-center px-4 text-[13px] font-semibold text-[color:var(--text-primary)]">Results</h2>
            <div className="flex min-h-0 flex-1 divide-x divide-[color:var(--border-default)] overflow-auto">
              {panels.map((panel) => (
                <OutputColumn key={panel.id} panel={panel} running={runningIds.includes(panel.id)} />
              ))}
            </div>
          </div>
        )}
      </div>

      {spanImportPanelId && (
        <SpanImportModal
          onClose={() => setSpanImportPanelId(null)}
          onApply={applyImportedSpan}
          hasExisting={(panels.find((p) => p.id === spanImportPanelId)?.messages || []).some((m) => (m.content || []).some((b) => b.text))}
        />
      )}

      {saveModal && (
        <SaveVersionModal
          draft={saveModal.draft}
          baseline={saveModal.baseline}
          existingName={saveModal.existingName}
          versions={saveModal.versions}
          teamLabels={teamLabels}
          onCreateLabel={async (l) => { const labels = await addLabel(l); setTeamLabels(labels); return l; }}
          onSave={doSaveVersion}
          onClose={() => setSaveModal(null)}
        />
      )}
    </div>
  );
}
